import { useEffect, useMemo, useState } from 'react'

type GroupTimerMetadata = {
  groupsDateEnd: number
  groupsStartTime?: number
  groupsDuration?: number
}

const formatRemainingTime = (diff: number) => {
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  if (hours > 0) {
    return `${hours}ч ${minutes}м ${seconds}с`
  }

  if (minutes > 0) {
    return `${minutes}м ${seconds}с`
  }

  return `${seconds}с`
}

const parseGroupTimerMetadata = (roomMetadata?: string): GroupTimerMetadata | null => {
  if (!roomMetadata) return null

  try {
    const metadata = JSON.parse(roomMetadata)
    const groupsDateEnd = metadata.groupsDateEnd
    const groupsStartTime = metadata.groupsStartTime
    const groupsDuration = metadata.groupsDuration

    if (typeof groupsDateEnd !== 'number') {
      return null
    }

    return {
      groupsDateEnd,
      groupsStartTime: typeof groupsStartTime === 'number' ? groupsStartTime : undefined,
      groupsDuration: typeof groupsDuration === 'number' ? groupsDuration : undefined,
    }
  } catch (error) {
    console.error('Error parsing group timer from metadata:', error)
    return null
  }
}

const calculateRemainingMs = (timerMetadata: GroupTimerMetadata) => {
  const now = Date.now()

  if (timerMetadata.groupsStartTime && timerMetadata.groupsDuration) {
    const elapsed = now - timerMetadata.groupsStartTime
    const remaining = timerMetadata.groupsDuration * 1000 - elapsed
    return Math.max(0, remaining)
  }

  return Math.max(0, timerMetadata.groupsDateEnd - now)
}

export const useGroupRemainingTime = (roomMetadata?: string, enabled = true) => {
  const timerMetadata = useMemo(() => parseGroupTimerMetadata(roomMetadata), [roomMetadata])
  const [remainingTime, setRemainingTime] = useState<string | null>(null)

  const hasActiveTimer = useMemo(() => {
    if (!timerMetadata) return false
    return calculateRemainingMs(timerMetadata) > 0
  }, [timerMetadata])

  useEffect(() => {
    if (!enabled || !timerMetadata) {
      setRemainingTime(null)
      return
    }

    const updateTimer = () => {
      const diff = calculateRemainingMs(timerMetadata)
      setRemainingTime(diff > 0 ? formatRemainingTime(diff) : null)
    }

    updateTimer()

    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [enabled, timerMetadata])

  return {
    hasActiveTimer,
    remainingTime,
  }
}
